import { useRef, useState } from 'react';
import type { ViewArea, PrintAreaDef } from '@/lib/product-config';

// Multi-zone visual placement canvas — the native port of pc-admin's
// MockupEditor. Renders the garment view image with one draggable/resizable
// box per placed print area. Coordinates are stored in NATIVE image pixels.
type Props = {
  imageSrc: string;
  areas: ViewArea[];
  printAreas: PrintAreaDef[];
  selectedAreaId: string | null;
  onSelect: (areaId: string | null) => void;
  onChange: (areas: ViewArea[]) => void;
};

// Stable zone tints by print-area index — readable on light + dark garments.
const ZONE_COLORS = ['#007aff', '#34c759', '#ff9500', '#af52de', '#ff2d55', '#5ac8fa'];

export function zoneColor(printAreas: PrintAreaDef[], areaId: string): string {
  const idx = Math.max(0, printAreas.findIndex((p) => p.id === areaId));
  return ZONE_COLORS[idx % ZONE_COLORS.length];
}

const MIN_ZONE = 12; // native px

function clampArea(a: ViewArea, natW: number, natH: number): ViewArea {
  const w = Math.min(Math.max(MIN_ZONE, a.w), natW);
  const h = Math.min(Math.max(MIN_ZONE, a.h), natH);
  return {
    ...a,
    w, h,
    x: Math.min(Math.max(0, a.x), natW - w),
    y: Math.min(Math.max(0, a.y), natH - h),
  };
}

export function ViewZoneCanvas({ imageSrc, areas, printAreas, selectedAreaId, onSelect, onChange }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [dispW, setDispW] = useState(0);
  const drag = useRef<null | {
    mode: 'move' | 'resize' | 'draw';
    areaId: string;
    startX: number;
    startY: number;
    orig: ViewArea;
  }>(null);

  const scale = nat && dispW ? dispW / nat.w : 1;
  const toNative = (px: number) => Math.round(px / scale);

  function startDrag(mode: 'move' | 'resize', areaId: string, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const orig = areas.find((a) => a.areaId === areaId);
    if (!orig) return;
    onSelect(areaId);
    drag.current = { mode, areaId, startX: e.clientX, startY: e.clientY, orig: { ...orig } };
    wrapRef.current?.setPointerCapture(e.pointerId);
  }

  // Drawing on empty canvas places the SELECTED print area (or replaces its box).
  function startDraw(e: React.PointerEvent) {
    if (!nat || !selectedAreaId || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = toNative(e.clientX - rect.left);
    const y = toNative(e.clientY - rect.top);
    const fresh: ViewArea = { areaId: selectedAreaId, x, y, w: MIN_ZONE, h: MIN_ZONE };
    const rest = areas.filter((a) => a.areaId !== selectedAreaId);
    onChange([...rest, fresh]);
    drag.current = { mode: 'draw', areaId: selectedAreaId, startX: e.clientX, startY: e.clientY, orig: fresh };
    wrapRef.current.setPointerCapture(e.pointerId);
  }

  function onMove(e: React.PointerEvent) {
    if (!drag.current || !nat) return;
    const d = drag.current;
    const dx = toNative(e.clientX - d.startX);
    const dy = toNative(e.clientY - d.startY);
    let next: ViewArea;
    if (d.mode === 'move') next = { ...d.orig, x: d.orig.x + dx, y: d.orig.y + dy };
    else next = { ...d.orig, w: d.orig.w + dx, h: d.orig.h + dy }; // resize + draw share the grow math
    next = clampArea(next, nat.w, nat.h);
    onChange(areas.map((a) => (a.areaId === d.areaId ? next : a)));
  }

  function endDrag() { drag.current = null; }

  return (
    <div
      ref={wrapRef}
      className="relative inline-block touch-none select-none overflow-hidden rounded-[10px] border border-black/[0.07] bg-[conic-gradient(at_25%_25%,#fafafa_90deg,#f1f1f3_90deg_180deg,#fafafa_180deg_270deg,#f1f1f3_270deg)] bg-[length:20px_20px]"
      onPointerDown={startDraw}
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <img
        src={imageSrc}
        alt="garment view"
        draggable={false}
        className="block max-h-[480px] max-w-full"
        onLoad={(e) => {
          setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
          setDispW(e.currentTarget.clientWidth);
        }}
      />
      {nat && areas.map((a) => {
        const color = zoneColor(printAreas, a.areaId);
        const pa = printAreas.find((p) => p.id === a.areaId);
        const selected = a.areaId === selectedAreaId;
        return (
          <div
            key={a.areaId}
            className="absolute cursor-move"
            style={{
              left: a.x * scale,
              top: a.y * scale,
              width: a.w * scale,
              height: a.h * scale,
              border: `2px solid ${color}`,
              background: `${color}${selected ? '2e' : '1a'}`,
              boxShadow: selected ? `0 0 0 2px ${color}55` : undefined,
              borderRadius: 4,
            }}
            onPointerDown={(e) => startDrag('move', a.areaId, e)}
          >
            <span
              className="absolute -top-5 left-0 whitespace-nowrap rounded-[4px] px-1.5 py-px text-[9.5px] font-semibold text-white"
              style={{ background: color }}
            >
              {pa?.label || pa?.position || a.areaId}
            </span>
            <div
              className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-se-resize rounded-[4px] border-2 border-white"
              style={{ background: color }}
              onPointerDown={(e) => startDrag('resize', a.areaId, e)}
            />
          </div>
        );
      })}
    </div>
  );
}
